import {Navigate} from 'react-router-dom';
import type {ReactNode} from 'react';
import {useWorkspaceModulesState, type WorkspaceModules} from '@api/workspaces';
import {ErrorState} from '@ui/primitives/ErrorState';

/**
 * Guard раздела, привязанного к модулю воркспейса.
 *
 * Скрыть пункт в сайдбаре недостаточно: на выключённый раздел ведут закладки,
 * прямые ссылки и история браузера. Отдельный компонент (а не ранний return
 * внутри страницы) нужен потому, что флаг приходит асинхронно — выход из
 * середины страницы ломал бы порядок хуков.
 *
 * Дефолт модулей — «выключено», поэтому редирект откладываем до загрузки
 * воркспейса: иначе прямая ссылка на включённый раздел уводила бы на дашборд.
 */
export const ModuleGuard = ({
  module,
  fallback,
  children,
}: {
  module: keyof WorkspaceModules;
  /**
   * Что показать при выключённом модуле. Без него — редирект на дашборд.
   * Используется для заглушек «модуль не подключён»: раздел остаётся доступен,
   * но вместо данных объясняет, как его включить.
   */
  fallback?: ReactNode;
  children: ReactNode;
}) => {
  const {modules, isPending, isError, retry} = useWorkspaceModulesState();
  if (isPending) return null;
  // Воркспейс не прочитался — модули неизвестны, а не выключены. Молчаливый
  // фолбэк соврал бы: заглушка «модуль не подключён» тому, у кого он включён.
  if (isError) {
    return (
      <ErrorState
        title='Не удалось загрузить настройки воркспейса'
        description='Проверьте подключение и нажмите «Повторить».'
        onRetry={retry}
      />
    );
  }
  if (!modules[module]) {
    return fallback ? <>{fallback}</> : <Navigate to='/dashboard' replace />;
  }
  return <>{children}</>;
};
